import type {
  Tour,
  TourAspectRatio,
  TourFrame,
  TourStep,
} from '@carboncopyco/types';
import { type Slice, createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { FramesWarnings } from '../../types/frames';
import type { StepWarning } from '../../types/step';

export interface TourEditorState {
  activeFrameId: number | null;
  activeStepId: string | null;
  addingStepInProgress: boolean;
  isGeneratingReplayFrameChunks: boolean;
  isSettingsModified: boolean;
  isApplyingTourTheme: boolean;
  focusFrameIndex: number | null;
  isScreenshotAsFrameUploading: boolean;
  capturedScreenSize: {
    width: number;
    height: number;
  } | null;
  aspectRatio: TourAspectRatio | null;
  playableFrames: TourFrame[];
  draftSettings?: Tour['draftSettings'];
  framesWarnings?: FramesWarnings;
  isMediaUploading: boolean;
}

interface TourEditorSlice {
  tours: {
    [uuid: string]: TourEditorState;
  };
}

type TourEditorAction<T = {}> = PayloadAction<T & { uuid: string }>;

const tourInitialState: TourEditorState = {
  activeFrameId: 0,
  activeStepId: null,
  addingStepInProgress: false,
  isGeneratingReplayFrameChunks: false,
  isApplyingTourTheme: false,
  isSettingsModified: false,
  focusFrameIndex: null,
  isScreenshotAsFrameUploading: false,
  capturedScreenSize: null,
  aspectRatio: null,
  playableFrames: [],
  draftSettings: null,
  framesWarnings: {},
  isMediaUploading: false,
};

const tourEditorInitialState: TourEditorSlice = {
  tours: {},
};

export const tourEditorSlice: Slice<TourEditorSlice> = createSlice({
  name: 'tourEditor',
  initialState: tourEditorInitialState,
  reducers: {
    setInitialTourEditorState: (state, action: TourEditorAction) => {
      state.tours[action.payload.uuid] = tourInitialState;
    },
    clearTourEditorState: (state, action: TourEditorAction) => {
      delete state.tours[action.payload.uuid];
    },
    setActiveFrameId: (
      state,
      action: TourEditorAction<{
        frameId: TourEditorState['activeFrameId'];
      }>
    ) => {
      state.tours[action.payload.uuid].activeFrameId = action.payload.frameId;
    },
    setActiveStepId: (
      state,
      action: TourEditorAction<{
        stepId: TourEditorState['activeStepId'];
      }>
    ) => {
      state.tours[action.payload.uuid].activeStepId = action.payload.stepId;
    },
    setAddingStepInProgress: (
      state,
      action: TourEditorAction<{
        inProgress: TourEditorState['addingStepInProgress'];
      }>
    ) => {
      state.tours[action.payload.uuid].addingStepInProgress =
        action.payload.inProgress;
    },
    setIsGeneratingReplayFrameChunks: (
      state,
      action: TourEditorAction<{
        isGenerating: TourEditorState['isGeneratingReplayFrameChunks'];
      }>
    ) => {
      state.tours[action.payload.uuid].isGeneratingReplayFrameChunks =
        action.payload.isGenerating;
    },
    setIsApplyingTourTheme: (
      state,
      action: TourEditorAction<{
        isApplyingTourTheme: TourEditorState['isApplyingTourTheme'];
      }>
    ) => {
      state.tours[action.payload.uuid].isApplyingTourTheme =
        action.payload.isApplyingTourTheme;
    },
    setIsSettingsModified: (
      state,
      action: TourEditorAction<{
        isModified: TourEditorState['isSettingsModified'];
      }>
    ) => {
      state.tours[action.payload.uuid].isSettingsModified =
        action.payload.isModified;
    },
    setFocusFrameIndex: (
      state,
      action: TourEditorAction<{
        frameIndex: TourEditorState['focusFrameIndex'];
      }>
    ) => {
      state.tours[action.payload.uuid].focusFrameIndex =
        action.payload.frameIndex;
    },
    setIsScreenshotAsFrameUploading: (
      state,
      action: TourEditorAction<{
        isUploading: TourEditorState['isScreenshotAsFrameUploading'];
      }>
    ) => {
      state.tours[action.payload.uuid].isScreenshotAsFrameUploading =
        action.payload.isUploading;
    },
    setCapturedScreenSize: (
      state,
      action: TourEditorAction<{
        size: TourEditorState['capturedScreenSize'];
      }>
    ) => {
      state.tours[action.payload.uuid].capturedScreenSize = action.payload.size;
    },
    setAspectRatio: (
      state,
      action: TourEditorAction<{
        aspectRatio: TourEditorState['aspectRatio'];
      }>
    ) => {
      state.tours[action.payload.uuid].aspectRatio = action.payload.aspectRatio;
    },
    setPlayableFrames: (
      state,
      action: TourEditorAction<{
        playableFrames: TourEditorState['playableFrames'];
      }>
    ) => {
      if (state.tours[action.payload.uuid]) {
        state.tours[action.payload.uuid].playableFrames =
          action.payload.playableFrames;
      }
    },
    setDraftSettings: (
      state,
      action: TourEditorAction<{
        draftSettings: Tour['draftSettings'];
      }>
    ) => {
      state.tours[action.payload.uuid].draftSettings =
        action.payload.draftSettings;
    },
    addStepWarnings: (
      state,
      action: TourEditorAction<{
        warnings: {
          frameId: TourFrame['id'];
          stepId: TourStep['stepId'];
          warning: StepWarning;
        }[];
      }>
    ) => {
      const { uuid } = action.payload;

      action.payload.warnings.forEach((element) => {
        const { frameId, stepId, warning } = element;

        const framesWarnings = state.tours[uuid].framesWarnings;

        if (!framesWarnings[frameId]) {
          framesWarnings[frameId] = {};
        }

        if (!framesWarnings[frameId][stepId]) {
          framesWarnings[frameId][stepId] = [];
        }

        if (framesWarnings[frameId][stepId].includes(warning)) {
          return;
        }

        framesWarnings[frameId][stepId].push(warning);
      });
    },
    removeStepWarnings: (
      state,
      action: TourEditorAction<{
        warnings: {
          frameId: TourFrame['id'];
          stepId: TourStep['stepId'];
          warning: StepWarning;
        }[];
      }>
    ) => {
      const { uuid } = action.payload;
      action.payload.warnings.forEach((element) => {
        const { frameId, stepId, warning } = element;

        const framesWarnings = state.tours[uuid].framesWarnings;

        if (!framesWarnings[frameId]) {
          return;
        }
        if (!framesWarnings[frameId][stepId]) {
          return;
        }

        framesWarnings[frameId][stepId] = framesWarnings[frameId][
          stepId
        ].filter((warn) => warn !== warning);
      });
    },
    resetStepWarnings: (state, action: TourEditorAction) => {
      state.tours[action.payload.uuid].framesWarnings =
        tourInitialState.framesWarnings;
    },
    setIsMediaUploading: (
      state,
      action: TourEditorAction<{
        isMediaUploading: TourEditorState['isMediaUploading'];
      }>
    ) => {
      state.tours[action.payload.uuid].isMediaUploading =
        action.payload.isMediaUploading;
    },
  },
});

export const {
  setInitialTourEditorState,
  clearTourEditorState,
  setActiveFrameId,
  setAddingStepInProgress,
  setIsGeneratingReplayFrameChunks,
  setIsApplyingTourTheme,
  setActiveStepId,
  setIsSettingsModified,
  setFocusFrameIndex,
  setIsScreenshotAsFrameUploading,
  setCapturedScreenSize,
  setAspectRatio,
  setPlayableFrames,
  setDraftSettings,
  addStepWarnings,
  removeStepWarnings,
  resetStepWarnings,
  setIsMediaUploading,
} = tourEditorSlice.actions;
